import { useState, useRef } from 'react';
import { useAuthStore } from '../../stores/authStore';
import { Input, Button } from 'antd';
import request from '../../utils/request';
import { showToast } from '../../utils/toast';
import './ProjectSelector.css';

// 判断元素是否在弹窗内容内
function isInsideModalContent(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return target.closest('.project-modal') !== null;
}

export default function ProjectSelector() {
  const { user, currentProject, setCurrentProject, fetchUserInfo } = useAuthStore();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);
  const mouseDownInsideContent = useRef(false);

  // 组织名编辑状态
  const [editingOrgId, setEditingOrgId] = useState<string | null>(null);
  const [editingOrgName, setEditingOrgName] = useState('');
  const [isSavingOrgName, setIsSavingOrgName] = useState(false);

  // 进入组织名编辑模式
  const startEditOrgName = (orgId: string, orgName: string) => {
    setEditingOrgId(orgId);
    setEditingOrgName(orgName);
  };

  // 保存组织名
  const saveOrgName = async () => {
    const trimmedName = editingOrgName.trim();
    if (!trimmedName || !editingOrgId) {
      setEditingOrgId(null);
      return;
    }
    // 找到当前编辑的组织，判断名称是否变化
    const targetOrg = organizations.find(o => o.id === editingOrgId);
    if (targetOrg && trimmedName === targetOrg.name) {
      setEditingOrgId(null);
      return;
    }
    setIsSavingOrgName(true);
    try {
      await request.put(`/gruser/organization/name?org_id=${editingOrgId}`, {
        name: trimmedName,
      });
      showToast('Organization name updated', 'success');
      // 刷新用户信息以获取最新组织名称
      await fetchUserInfo();
      setEditingOrgId(null);
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to update organization name';
      showToast(message, 'error');
    } finally {
      setIsSavingOrgName(false);
    }
  };

  // 取消编辑
  const cancelEditOrgName = () => {
    setEditingOrgId(null);
    setEditingOrgName('');
  };

  const projects = user?.projects || [];
  const organizations = user?.organizations || [];

  // Get organization name by ID
  const getOrgName = (orgId: string) => {
    const org = organizations.find(o => o.id === orgId);
    return org?.name || 'Unknown Organization';
  };

  // Group projects by organization
  const groupedProjects = organizations
    .map(org => ({
      ...org,
      projects: projects.filter(p => p.organization_id === org.id),
    }))
    .filter(org => org.projects.length > 0);

  const handleSelectProject = (project: typeof currentProject) => {
    if (project && project.id !== currentProject?.id) {
      setCurrentProject(project);
    }
    setIsModalOpen(false);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    // 关闭弹窗时重置编辑状态
    setEditingOrgId(null);
    setEditingOrgName('');
  };

  if (projects.length === 0) {
    return null;
  }

  return (
    <>
      <div className="project-selector">
        <button
          className="project-selector-trigger"
          onClick={() => setIsModalOpen(true)}
          title="Click to switch project"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M12 2H2v10h10V2z" />
            <path d="M22 2h-10v10h10V2z" />
            <path d="M12 12H2v10h10V12z" />
            <path d="M22 12h-10v10h10V12z" />
          </svg>
          <span className="project-display">
            <span className="org-name">
              {currentProject ? getOrgName(currentProject.organization_id) : ''}
            </span>
            <span className="separator">/</span>
            <span className="project-name">{currentProject?.name || 'Select Project'}</span>
          </span>
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="dropdown-arrow"
          >
            <path d="m6 9 6 6 6-6" />
          </svg>
        </button>
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div
          ref={overlayRef}
          className="project-modal-overlay"
          onMouseDown={e => {
            mouseDownInsideContent.current = isInsideModalContent(e.target);
          }}
          onClick={e => {
            if (e.target === overlayRef.current && !mouseDownInsideContent.current) {
              handleCloseModal();
            }
          }}
        >
          <div className="project-modal">
            <div className="project-modal-header">
              <h3 className="project-modal-title">Select Project</h3>
              <button className="project-modal-close" onClick={handleCloseModal}>
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>
            <div className="project-modal-body">
              {groupedProjects.map(org => (
                <div key={org.id} className="project-org-group">
                  <div className="project-org-header">
                    {editingOrgId === org.id ? (
                      /* 编辑模式：显示输入框和操作按钮 */
                      <div className="flex flex-1 flex-col gap-3">
                        <Input
                          size="small"
                          autoFocus
                          value={editingOrgName}
                          onChange={e => setEditingOrgName(e.target.value)}
                          onKeyDown={e => {
                            if (e.key === 'Enter') saveOrgName();
                            if (e.key === 'Escape') cancelEditOrgName();
                          }}
                          disabled={isSavingOrgName}
                          maxLength={100}
                        />
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="small"
                            onClick={cancelEditOrgName}
                            disabled={isSavingOrgName}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="primary"
                            size="small"
                            onClick={saveOrgName}
                            loading={isSavingOrgName}
                          >
                            Save
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* 非编辑模式：显示组织名和角色 */
                      <>
                        <span className="project-org-name">{org.name}</span>
                        {org.type === 'personal' && (
                          <span className="ml-1 inline-block rounded bg-indigo-100 px-1.5 py-0.5 text-xs text-indigo-600">
                            Personal
                          </span>
                        )}
                        {/* 管理员才显示编辑图标 */}
                        {org.role === 'admin' && (
                          <svg
                            className="cursor-pointer text-gray-400 hover:text-indigo-500"
                            width="13"
                            height="13"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            onClick={e => {
                              e.stopPropagation();
                              startEditOrgName(org.id, org.name);
                            }}
                          >
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        )}
                        <span className="project-org-role ml-auto">{org.role}</span>
                      </>
                    )}
                  </div>
                  <div className="project-org-list">
                    {org.projects.map(project => (
                      <button
                        key={project.id}
                        className={`project-modal-item ${currentProject?.id === project.id ? 'active' : ''}`}
                        onClick={() => handleSelectProject(project)}
                      >
                        <div className="project-modal-item-info">
                          <span className="project-modal-item-name">{project.name}</span>
                          <span className="project-modal-item-role">{project.role}</span>
                        </div>
                        {currentProject?.id === project.id && (
                          <svg
                            width="18"
                            height="18"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className="check-icon"
                          >
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
